/**
 * ComponentGeneratorService
 *
 * Generates images/videos for individual screen components (not whole screens).
 * Each component has its own prompt, reference images, model, and count settings.
 * Results stream in progressively via async generators.
 */

import type { ScreenComponentDef, ComponentResult, ComponentFormat } from '@core/types/slot-screen-component.types';
import type { ImageGenerationSettings, ReferenceImage, ImageModelId } from '../../../core/types/image-generation.types';
import { generateImages } from '../image-generation/ImageGeneratorService';
import { buildSystemContext } from './SlotScreenGeneratorService';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';

const generateId = () => Math.random().toString(36).substring(2, 11);

// ─── Delay Helper ──────────────────────────────────────────

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

const STAGGER_MS = 1500;

// ─── Prompt Building ──────────────────────────────────────

/**
 * Derive the format instruction for a component based on its format type.
 */
function formatInstruction(format: ComponentFormat): string {
  switch (format) {
    case 'transparent':
      return 'PNG with transparent background, element isolated on alpha';
    case 'opaque':
      return 'Full opaque image filling the entire canvas';
    case 'text':
      return 'Text rendering on transparent background';
    default:
      return 'PNG with transparent background, element isolated on alpha';
  }
}

/**
 * Build the prompt for a single component within a screen.
 * Returns separate systemContext (for Gemini systemInstruction) and userPrompt.
 */
export function buildComponentPrompt(
  systemPromptJson: string,
  screenName: string,
  screenDescription: string,
  component: ScreenComponentDef,
): { systemContext: string; userPrompt: string } {
  const systemContext = buildSystemContext(systemPromptJson);

  const parts: string[] = [];

  // Screen context
  if (screenDescription) {
    parts.push(`Screen: ${screenName} — ${screenDescription}`);
  } else {
    parts.push(`Screen: ${screenName}`);
  }

  // Component target
  parts.push(`Generate ONLY the UI component: ${component.name}`);

  // Component description
  if (component.description) {
    parts.push(`Description: ${component.description}`);
  }

  // Dimensions
  parts.push(`Size: ${component.width}x${component.height}px`);

  // Format instruction
  parts.push(`Format: ${formatInstruction(component.format)}`);

  // User prompt (custom instructions)
  if (component.userPrompt.trim()) {
    parts.push(component.userPrompt.trim());
  } else if (component.defaultPromptHint) {
    parts.push(component.defaultPromptHint);
  }

  const userPrompt = parts.join('\n');
  return { systemContext, userPrompt };
}

// ─── Aspect Ratio ─────────────────────────────────────────

type SupportedAspectRatio = '1:1' | '16:9' | '9:16' | '3:4' | '4:3' | '3:2' | '2:3';

/**
 * Derive the closest supported aspect ratio from width and height.
 */
export function deriveAspectRatio(w: number, h: number): SupportedAspectRatio {
  const ratio = w / h;
  if (Math.abs(ratio - 1) < 0.1) return '1:1';
  if (Math.abs(ratio - 16 / 9) < 0.05) return '16:9';
  if (Math.abs(ratio - 9 / 16) < 0.05) return '9:16';
  if (Math.abs(ratio - 3 / 4) < 0.05) return '3:4';
  if (Math.abs(ratio - 4 / 3) < 0.05) return '4:3';
  if (Math.abs(ratio - 3 / 2) < 0.05) return '3:2';
  if (Math.abs(ratio - 2 / 3) < 0.05) return '2:3';
  // Fallback based on orientation
  if (Math.abs(ratio - 1) < 0.2) return '1:1';
  return h > w ? '9:16' : '16:9';
}

// ─── Image Generation ─────────────────────────────────────

/**
 * Build ImageGenerationSettings for a component generation request.
 */
function buildComponentImageSettings(
  systemContext: string,
  userPrompt: string,
  component: ScreenComponentDef,
  model: string,
): ImageGenerationSettings {
  const referenceImages: ReferenceImage[] = component.referenceImages.map((url, i) => ({
    id: `comp-ref-${i}`,
    url,
    fileName: `reference-${i + 1}.png`,
    source: 'upload' as const,
  }));

  return {
    prompt: component.userPrompt || component.defaultPromptHint,
    effectivePrompt: userPrompt,
    systemInstruction: systemContext,
    model: model as ImageModelId,
    width: component.width,
    height: component.height,
    style: 'realistic',
    qualityPreset: '1K',
    batchCount: 1,
    aspectRatio: deriveAspectRatio(component.width, component.height),
    seedMode: 'auto',
    styleStrength: 0.5,
    referenceImages,
    removeBackground: component.format === 'transparent' || component.format === 'text',
  };
}

/**
 * Generate component images with streaming results via AsyncGenerator.
 * Yields each result as it arrives, enabling progressive UI updates.
 */
export async function* generateComponentImages(
  systemPromptJson: string,
  screenName: string,
  screenDescription: string,
  component: ScreenComponentDef,
  settings: { model: string; imageCount: number },
  signal?: AbortSignal,
): AsyncGenerator<{ index: number; result: ComponentResult }> {
  const { systemContext, userPrompt } = buildComponentPrompt(
    systemPromptJson, screenName, screenDescription, component,
  );

  for (let i = 0; i < settings.imageCount; i++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Stagger requests to avoid rate limits
    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    const id = generateId();
    const timestamp = Date.now();

    try {
      const imageSettings = buildComponentImageSettings(
        systemContext, userPrompt, component, settings.model,
      );
      const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;

      const results = await generateImages({
        settings: { ...imageSettings, batchCount: 1 },
        apiKeys,
        signal,
      });

      const genResult = results[0];
      if (!genResult || genResult.status === 'failed') {
        yield {
          index: i,
          result: {
            id,
            type: 'image',
            imageUrl: '',
            savedPath: '',
            status: 'error',
            error: genResult?.error ?? 'Generation failed',
            timestamp,
            prompt: component.userPrompt || component.defaultPromptHint,
            model: settings.model,
            width: component.width,
            height: component.height,
          },
        };
        continue;
      }

      yield {
        index: i,
        result: {
          id,
          type: 'image',
          imageUrl: genResult.url,
          savedPath: '',
          status: 'ready',
          timestamp,
          prompt: component.userPrompt || component.defaultPromptHint,
          model: settings.model,
          width: component.width,
          height: component.height,
        },
      };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      yield {
        index: i,
        result: {
          id,
          type: 'image',
          imageUrl: '',
          savedPath: '',
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
          timestamp,
          prompt: component.userPrompt || component.defaultPromptHint,
          model: settings.model,
          width: component.width,
          height: component.height,
        },
      };
    }
  }
}

// ─── Video Generation (Stub) ──────────────────────────────

/**
 * Generate component videos.
 * Currently a stub — full implementation will use Veo via VideoCompilerService.
 * Yields error results with a "not yet implemented" message.
 */
export async function* generateComponentVideos(
  _systemPromptJson: string,
  _screenName: string,
  _screenDescription: string,
  component: ScreenComponentDef,
  settings: { model: string; imageCount: number },
  signal?: AbortSignal,
): AsyncGenerator<{ index: number; result: ComponentResult }> {
  for (let i = 0; i < settings.imageCount; i++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    yield {
      index: i,
      result: {
        id: generateId(),
        type: 'video',
        imageUrl: '',
        videoUrl: '',
        savedPath: '',
        status: 'error',
        error: 'Video generation not yet implemented',
        timestamp: Date.now(),
        prompt: component.userPrompt || component.defaultPromptHint,
        model: settings.model,
        width: component.width,
        height: component.height,
      },
    };
  }
}
